// Small trigger button + popover for inserting a merge-tag string into a
// prop. Dropped next to any text-ish input in the properties panel. Three
// tabs:
//
//   Brand     $brand.X paths available on the current brand profile
//   Slot      $slot.X — existing slots + "create new slot"
//   Merge Tag [NM:X] — common send-time merge tags
//
// Insert semantics are up to the caller: pass `onInsert(token)` and the
// caller decides whether to replace the whole value (color, URL) or insert
// at cursor (text content).

import type {
  SlotKind,
  SlotSchema,
  SlotDefault,
} from '@nexmail/shared-util/email-builder';
import { MergeTag, nmToken } from '@nexmail/shared-util/email-builder';
import type { MergeTagInfoDto } from '@/apis/backend/generated/models/mergeTagInfoDto';

import { useTranslation } from 'react-i18next';
import { useRef, useMemo, useState } from 'react';
import { useOrganization } from '@/context/organization-context';
import { useEspExportControllerGetMergeTags } from '@/apis/backend/generated/esp-export/esp-export';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import {
  Add as AddIcon,
  DataObject as TokenIcon,
} from '@mui/icons-material';

import { useDynamicPicker, type DynamicPickerSlotEntry } from './context';
import { TEXT, BORDER, INDIGO, TEXT_DIM, INDIGO_TEXT } from '../constants';

export interface InsertDynamicButtonProps {
  onInsert: (token: string) => void;
  // Restrict which tabs are shown based on the input's nature. Color and
  // URL inputs don't usually want [NM:X] (send-time tags are for content).
  allowMergeTags?: boolean;
  // Small-size variant for inline use alongside inputs.
  compact?: boolean;
}

// Covers the loading and error paths of the fetch in MergeTagTab below, so the
// tab never renders empty before GET .../esp/merge-tags resolves or if it
// fails.
//
// Every entry names a MergeTag member and lets nmToken spell the token, so a
// tag that does not exist fails type-check rather than shipping. These were
// hand-typed strings while the enum lived in api, out of web's reach, and two
// of the six were wrong: [NM:VIEW_IN_BROWSER] and [NM:PREFERENCES_URL] named
// nothing, the exporter passed them through untouched, and recipients saw the
// brackets. The enum now lives in shared-util, so the list can be built from it
// instead of checked against it by hand.
const FALLBACK_MERGE_TAGS = (
  [
    { id: MergeTag.UNSUBSCRIBE_URL, hintKey: 'mergeTags.unsubscribeUrl' },
    { id: MergeTag.VIEW_IN_BROWSER_URL, hintKey: 'mergeTags.viewInBrowser' },
    { id: MergeTag.FIRST_NAME, hintKey: 'mergeTags.firstName' },
    { id: MergeTag.LAST_NAME, hintKey: 'mergeTags.lastName' },
    { id: MergeTag.EMAIL, hintKey: 'mergeTags.email' },
    { id: MergeTag.MANAGE_PREFERENCES_URL, hintKey: 'mergeTags.preferencesUrl' },
    // `satisfies` rather than a `:` annotation or a cast: it checks each entry
    // without widening `id` to string, so a bad tag is reported on the line
    // that wrote it instead of at the nmToken call below.
  ] satisfies { id: MergeTag; hintKey: string }[]
).map(({ id, hintKey }) => ({ tag: nmToken(id), hintKey }));

// Maps a server-returned MergeTag id to the i18n key carrying its translated
// hint, for the tags we have real copy for (including Spanish). A tag the
// server returns with no entry here falls back to its English `description`
// from the API, which is better than nothing but stays untranslated until
// someone adds a key and translations for it.
// Keyed by the enum, so an entry for a tag that does not exist fails
// type-check. The lookup itself still takes a plain string, because `id` comes
// off the wire and the server is the one claiming it is a MergeTag.
const MERGE_TAG_HINT_KEYS: Partial<Record<MergeTag, string>> = {
  [MergeTag.UNSUBSCRIBE_URL]: 'mergeTags.unsubscribeUrl',
  [MergeTag.VIEW_IN_BROWSER_URL]: 'mergeTags.viewInBrowser',
  [MergeTag.MANAGE_PREFERENCES_URL]: 'mergeTags.preferencesUrl',
  [MergeTag.FIRST_NAME]: 'mergeTags.firstName',
  [MergeTag.LAST_NAME]: 'mergeTags.lastName',
  [MergeTag.EMAIL]: 'mergeTags.email',
};

function mergeTagHintKey(id: string): string | undefined {
  return (MERGE_TAG_HINT_KEYS as Record<string, string | undefined>)[id];
}

// Ordered so the common colors + heading surface at the top of the Brand tab.
const BRAND_PATH_ORDER: { path: string; labelKey: string; sample: (b: NonNullable<ReturnType<typeof useDynamicPicker>>['brand']) => string | undefined }[] = [
  { path: '$brand.name', labelKey: 'brandPaths.name', sample: (b) => b?.name },
  { path: '$brand.logoUrl', labelKey: 'brandPaths.logoUrl', sample: (b) => b?.logoUrl },
  { path: '$brand.borderRadius', labelKey: 'brandPaths.borderRadius', sample: (b) => b?.borderRadius },
  { path: '$brand.tagline', labelKey: 'brandPaths.tagline', sample: (b) => b?.tagline },
  { path: '$brand.website', labelKey: 'brandPaths.website', sample: (b) => b?.website },
  { path: '$brand.email', labelKey: 'brandPaths.email', sample: (b) => b?.email },
  { path: '$brand.address', labelKey: 'brandPaths.address', sample: (b) => b?.address },
  { path: '$brand.description', labelKey: 'brandPaths.description', sample: (b) => b?.description },
  { path: '$brand.color.primary', labelKey: 'brandPaths.colorPrimary', sample: (b) => b?.colorByUsage.primary },
  { path: '$brand.color.secondary', labelKey: 'brandPaths.colorSecondary', sample: (b) => b?.colorByUsage.secondary },
  { path: '$brand.color.accent', labelKey: 'brandPaths.colorAccent', sample: (b) => b?.colorByUsage.accent },
  { path: '$brand.color.background', labelKey: 'brandPaths.colorBackground', sample: (b) => b?.colorByUsage.background },
  { path: '$brand.color.text', labelKey: 'brandPaths.colorText', sample: (b) => b?.colorByUsage.text },
  { path: '$brand.color.button', labelKey: 'brandPaths.colorButton', sample: (b) => b?.colorByUsage.button },
  { path: '$brand.font.heading', labelKey: 'brandPaths.fontHeading', sample: (b) => b?.fontByUsage.heading ?? b?.fontByUsage.heading_1 },
  { path: '$brand.font.body', labelKey: 'brandPaths.fontBody', sample: (b) => b?.fontByUsage.body },
];

export function InsertDynamicButton({
  onInsert,
  allowMergeTags = true,
  compact = true,
}: InsertDynamicButtonProps) {
  const { t } = useTranslation('emailBuilder');
  const picker = useDynamicPicker();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'brand' | 'slot' | 'merge' | 'auto'>('brand');
  const [search, setSearch] = useState('');

  // If no context is mounted, the button is a no-op (nothing to offer).
  // Happens in isolated tests or if the view forgets the provider.
  if (!picker) return null;

  const hasSlotCallback = Boolean(picker.onRequestAddSlot);

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  const pick = (token: string) => {
    onInsert(token);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('insertDynamicButton.tooltipInsert', 'Insert dynamic value')} placement="top">
        <IconButton
          ref={anchorRef}
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            p: compact ? 0.25 : 0.5,
            color: TEXT_DIM,
            '&:hover': { color: INDIGO },
          }}
        >
          <TokenIcon sx={{ fontSize: compact ? 14 : 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { width: 360, maxHeight: 440, display: 'flex', flexDirection: 'column' } },
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: `1px solid ${BORDER}`, minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 11, textTransform: 'none' } }}
        >
          <Tab label={t('insertDynamicButton.tabs.brand', 'Brand')} value="brand" />
          <Tab label={t('insertDynamicButton.tabs.slot', 'Slot')} value="slot" />
          <Tab label={t('insertDynamicButton.tabs.auto', 'Auto')} value="auto" />
          {allowMergeTags && <Tab label={t('insertDynamicButton.tabs.mergeTag', 'Merge Tag')} value="merge" />}
        </Tabs>

        <Box sx={{ px: 1.5, py: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('insertDynamicButton.searchPlaceholder', 'Search…')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {tab === 'brand' && (
            <BrandTab
              brand={picker.brand}
              search={search}
              onPick={pick}
            />
          )}
          {tab === 'slot' && (
            <SlotTab
              slots={picker.slots}
              search={search}
              onPick={pick}
              onAddSlot={hasSlotCallback ? (entry) => {
                const finalName = picker.onRequestAddSlot!(entry);
                pick(`$slot.${finalName}`);
              } : undefined}
            />
          )}
          {tab === 'auto' && (
            <AutoTab brand={picker.brand} slots={picker.slots} search={search} onPick={pick} />
          )}
          {tab === 'merge' && allowMergeTags && (
            <MergeTagTab search={search} onPick={pick} />
          )}
        </Box>
      </Popover>
    </>
  );
}

// ---- Brand tab -------------------------------------------------------------

function BrandTab({
  brand,
  search,
  onPick,
}: {
  brand: ReturnType<typeof useDynamicPicker> extends infer T ? T extends { brand: infer B } ? B : never : never;
  search: string;
  onPick: (token: string) => void;
}) {
  const { t } = useTranslation('emailBuilder');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return BRAND_PATH_ORDER.filter((r) => {
      if (!q) return true;
      return r.path.toLowerCase().includes(q) || t(`insertDynamicButton.${r.labelKey}`, r.labelKey).toLowerCase().includes(q);
    });
  }, [search, t]);

  if (!brand) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('insertDynamicButton.brand.noBrand', 'No brand profile loaded. Select a client with a brand set up to use $brand tokens.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {rows.map((r) => {
        const sample = r.sample(brand);
        const label = t(`insertDynamicButton.${r.labelKey}`, r.labelKey);
        return (
          <Box
            key={r.path}
            onClick={() => onPick(r.path)}
            sx={{
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              borderBottom: `1px solid ${BORDER}`,
              '&:last-of-type': { borderBottom: 'none' },
              '&:hover': { bgcolor: '#F5F5FB' },
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, fontFamily: 'ui-monospace, monospace' }}>
              {r.path}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_DIM, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}{sample ? ` · ${sample}` : ` · ${t('insertDynamicButton.brand.notSet', 'not set')}`}
            </Typography>
          </Box>
        );
      })}
      {rows.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">{t('insertDynamicButton.noMatches', 'No matches')}</Typography>
        </Box>
      )}
    </Box>
  );
}

// ---- Slot tab --------------------------------------------------------------

function SlotTab({
  slots,
  search,
  onPick,
  onAddSlot,
}: {
  slots: DynamicPickerSlotEntry[];
  search: string;
  onPick: (token: string) => void;
  onAddSlot?: (entry: DynamicPickerSlotEntry) => void;
}) {
  const { t } = useTranslation('emailBuilder');
  const [adding, setAdding] = useState(false);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return slots;
    return slots.filter((s) => s.name.toLowerCase().includes(q));
  }, [slots, search]);

  if (adding && onAddSlot) {
    return <CreateSlotForm onCancel={() => setAdding(false)} onCreate={(entry) => {
      onAddSlot(entry);
      setAdding(false);
    }} />;
  }

  return (
    <Box>
      {visible.map((s) => (
        <Box
          key={s.name}
          onClick={() => onPick(`$slot.${s.name}`)}
          sx={{
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            borderBottom: `1px solid ${BORDER}`,
            '&:last-of-type': { borderBottom: 'none' },
            '&:hover': { bgcolor: '#F5F5FB' },
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, fontFamily: 'ui-monospace, monospace' }}>
            $slot.{s.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_DIM, mt: 0.25 }}>
            {s.schema.kind}
            {s.schema.hint ? ` · ${s.schema.hint}` : ''}
          </Typography>
        </Box>
      ))}

      {visible.length === 0 && !onAddSlot && (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('insertDynamicButton.slot.noSlots', 'No slots in this context.')}
          </Typography>
        </Box>
      )}

      {onAddSlot && (
        <Box sx={{ p: 1.5, borderTop: slots.length > 0 ? `1px solid ${BORDER}` : 'none' }}>
          <Button
            fullWidth
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            sx={{ fontSize: 12, textTransform: 'none', color: INDIGO_TEXT, justifyContent: 'flex-start' }}
          >
            {t('insertDynamicButton.slot.createNew', 'Create new slot')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function CreateSlotForm({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (entry: DynamicPickerSlotEntry) => void;
}) {
  const { t } = useTranslation('emailBuilder');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<SlotKind>('text');
  const [hint, setHint] = useState('');
  const [defaultText, setDefaultText] = useState('Your text here');
  const [imageStrategy, setImageStrategy] = useState<'recent-media' | 'placeholder'>('recent-media');
  const [productStrategy, setProductStrategy] = useState<'recent' | 'best-seller' | 'random'>('recent');
  const [linkStrategy, setLinkStrategy] = useState<'brand-homepage' | 'none'>('brand-homepage');

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const schema: SlotSchema = { kind, required: true, ...(hint ? { hint } : {}) };
    let def: SlotDefault;
    if (kind === 'text' || kind === 'rich-text') {
      def = { kind, value: defaultText };
    } else if (kind === 'image') {
      def = { kind: 'image', strategy: imageStrategy };
    } else if (kind === 'product') {
      def = { kind: 'product', strategy: productStrategy };
    } else {
      def = { kind: 'link', strategy: linkStrategy };
    }
    onCreate({ name: trimmed, schema, default: def });
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={1.25}>
        <TextField
          label={t('insertDynamicButton.createSlot.slotName', 'Slot name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          autoFocus
          placeholder={t('insertDynamicButton.createSlot.slotNamePlaceholder', 'e.g. headline')}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>{t('insertDynamicButton.createSlot.kind', 'Kind')}</InputLabel>
          <Select label={t('insertDynamicButton.createSlot.kind', 'Kind')} value={kind} onChange={(e) => setKind(e.target.value as SlotKind)}>
            <MenuItem value="text">text</MenuItem>
            <MenuItem value="rich-text">rich-text</MenuItem>
            <MenuItem value="image">image</MenuItem>
            <MenuItem value="product">product</MenuItem>
            <MenuItem value="link">link</MenuItem>
          </Select>
        </FormControl>

        {(kind === 'text' || kind === 'rich-text') && (
          <TextField
            label={t('insertDynamicButton.createSlot.defaultText', 'Default text')}
            value={defaultText}
            onChange={(e) => setDefaultText(e.target.value)}
            size="small"
          />
        )}
        {kind === 'image' && (
          <FormControl size="small" fullWidth>
            <InputLabel>{t('insertDynamicButton.createSlot.imageStrategy', 'Image strategy')}</InputLabel>
            <Select label={t('insertDynamicButton.createSlot.imageStrategy', 'Image strategy')} value={imageStrategy} onChange={(e) => setImageStrategy(e.target.value as 'recent-media' | 'placeholder')}>
              <MenuItem value="recent-media">{t('insertDynamicButton.createSlot.imageStrategyRecentMedia', 'Recent media')}</MenuItem>
              <MenuItem value="placeholder">{t('insertDynamicButton.createSlot.imageStrategyPlaceholder', 'Placeholder')}</MenuItem>
            </Select>
          </FormControl>
        )}
        {kind === 'product' && (
          <FormControl size="small" fullWidth>
            <InputLabel>{t('insertDynamicButton.createSlot.productStrategy', 'Product strategy')}</InputLabel>
            <Select label={t('insertDynamicButton.createSlot.productStrategy', 'Product strategy')} value={productStrategy} onChange={(e) => setProductStrategy(e.target.value as 'recent' | 'best-seller' | 'random')}>
              <MenuItem value="recent">{t('insertDynamicButton.createSlot.productStrategyRecent', 'Recent')}</MenuItem>
              <MenuItem value="best-seller">{t('insertDynamicButton.createSlot.productStrategyBestSeller', 'Best-seller')}</MenuItem>
              <MenuItem value="random">{t('insertDynamicButton.createSlot.productStrategyRandom', 'Random')}</MenuItem>
            </Select>
          </FormControl>
        )}
        {kind === 'link' && (
          <FormControl size="small" fullWidth>
            <InputLabel>{t('insertDynamicButton.createSlot.linkStrategy', 'Link strategy')}</InputLabel>
            <Select label={t('insertDynamicButton.createSlot.linkStrategy', 'Link strategy')} value={linkStrategy} onChange={(e) => setLinkStrategy(e.target.value as 'brand-homepage' | 'none')}>
              <MenuItem value="brand-homepage">{t('insertDynamicButton.createSlot.linkStrategyBrandHomepage', 'Brand homepage')}</MenuItem>
              <MenuItem value="none">{t('insertDynamicButton.createSlot.linkStrategyNone', 'None (empty)')}</MenuItem>
            </Select>
          </FormControl>
        )}

        <TextField
          label={t('insertDynamicButton.createSlot.hint', 'Hint (optional)')}
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          size="small"
        />

        <Divider />

        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={onCancel} sx={{ flex: 1, textTransform: 'none' }}>
            {t('common:cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={submit}
            disabled={!name.trim()}
            sx={{ flex: 1, textTransform: 'none' }}
          >
            {t('insertDynamicButton.createSlot.createAndInsert', 'Create & insert')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

// ---- Auto tab --------------------------------------------------------------

// Lets the author pick a "contrast against …" target — usually a brand color
// or slot that resolves to a hex. Emits `$auto.contrast(<inner>)` which the
// resolver replaces with white or near-black at insert time based on WCAG
// luminance of the inner value.
function AutoTab({
  brand,
  slots,
  search,
  onPick,
}: {
  brand: ReturnType<typeof useDynamicPicker> extends infer T ? T extends { brand: infer B } ? B : never : never;
  slots: DynamicPickerSlotEntry[];
  search: string;
  onPick: (token: string) => void;
}) {
  const { t } = useTranslation('emailBuilder');
  // Flatten the brand's color usages to picker options. Slots also surface
  // here so a designer can contrast against a slot-bound color.
  const brandColorOptions = useMemo(() => {
    if (!brand) return [];
    return Object.keys(brand.colorByUsage ?? {})
      .sort()
      .map((usage) => ({
        inner: `$brand.color.${usage}`,
        label: t('insertDynamicButton.auto.brandColorLabel', 'Brand · {{usage}}', { usage }),
        sample: brand.colorByUsage?.[usage],
      }));
  }, [brand, t]);

  const slotOptions = useMemo(
    () =>
      slots
        .filter((s) => s.schema.kind === 'text' || s.schema.kind === 'rich-text')
        .map((s) => ({
          inner: `$slot.${s.name}`,
          label: t('insertDynamicButton.auto.slotLabel', 'Slot · {{name}}', { name: s.name }),
          sample: undefined as string | undefined,
        })),
    [slots, t],
  );

  const rows = useMemo(() => {
    const all = [...brandColorOptions, ...slotOptions];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) => r.label.toLowerCase().includes(q) || r.inner.toLowerCase().includes(q));
  }, [brandColorOptions, slotOptions, search]);

  return (
    <Box>
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#FAFAFA', borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 11, color: TEXT_DIM }}>
          {t('insertDynamicButton.auto.description', 'Inserts')} <Box component="code" sx={{ fontSize: 10 }}>$auto.contrast(…)</Box> {t('insertDynamicButton.auto.descriptionSuffix', '— resolves to black or white at insert time based on the chosen background.')}
        </Typography>
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('insertDynamicButton.auto.empty', 'No brand colors or text slots to contrast against yet.')}
          </Typography>
        </Box>
      ) : (
        rows.map((r) => (
          <Box
            key={r.inner}
            onClick={() => onPick(`$auto.contrast(${r.inner})`)}
            sx={{
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              borderBottom: `1px solid ${BORDER}`,
              '&:last-of-type': { borderBottom: 'none' },
              '&:hover': { bgcolor: '#F5F5FB' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {r.sample && (
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 0.5,
                  border: `1px solid ${BORDER}`,
                  bgcolor: r.sample,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, fontFamily: 'ui-monospace, monospace' }}>
                $auto.contrast({r.inner})
              </Typography>
              <Typography sx={{ fontSize: 10, color: TEXT_DIM }}>
                {r.label}
              </Typography>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

// ---- Merge tag tab ---------------------------------------------------------

interface MergeTagPickerItem {
  tag: string;
  hint: string;
}

// Translated hint for a server-returned tag: real copy (Spanish included)
// where MERGE_TAG_HINT_KEYS knows the id, else the API's English description.
function mergeTagHint(info: MergeTagInfoDto, t: (key: string, fallback: string) => string): string {
  const hintKey = mergeTagHintKey(info.id);
  return hintKey ? t(`insertDynamicButton.${hintKey}`, info.description) : info.description;
}

function MergeTagTab({ search, onPick }: { search: string; onPick: (token: string) => void }) {
  const { t } = useTranslation('emailBuilder');
  const { activeOrganization, activeClient } = useOrganization();

  // Compliance + personalization tags from the backend's MergeTag enum,
  // the same registry the ESP exporter transforms at send time, so a tag
  // offered here can never drift from one that's actually recognized.
  // Brand-links (TERMS_URL etc.) are deliberately not returned by this
  // endpoint: no ESP has a native token for them, they always resolve to
  // "#", so they don't belong in a picker meant for working links.
  const { data } = useEspExportControllerGetMergeTags(
    activeOrganization?.id ?? '',
    activeClient?.id ?? '',
    {
      query: {
        enabled: Boolean(activeOrganization?.id && activeClient?.id),
        staleTime: Infinity,
      },
    },
  );

  const fallbackItems = useMemo<MergeTagPickerItem[]>(
    () => FALLBACK_MERGE_TAGS.map((item) => ({ tag: item.tag, hint: t(`insertDynamicButton.${item.hintKey}`, item.hintKey) })),
    [t],
  );

  // Undefined while loading or on a failed fetch; either way, fall back
  // rather than show an empty tab.
  const items = useMemo<MergeTagPickerItem[]>(() => {
    if (!data) return fallbackItems;
    return [...data.compliance, ...data.personalization].map((info) => ({
      tag: info.placeholder,
      hint: mergeTagHint(info, t),
    }));
  }, [data, fallbackItems, t]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.tag.toLowerCase().includes(q) || item.hint.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <Box>
      {visible.map((item) => (
        <Box
          key={item.tag}
          onClick={() => onPick(item.tag)}
          sx={{
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            borderBottom: `1px solid ${BORDER}`,
            '&:last-of-type': { borderBottom: 'none' },
            '&:hover': { bgcolor: '#F5F5FB' },
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT, fontFamily: 'ui-monospace, monospace' }}>
            {item.tag}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_DIM, mt: 0.25 }}>{item.hint}</Typography>
        </Box>
      ))}
      {visible.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">{t('insertDynamicButton.noMatches', 'No matches')}</Typography>
        </Box>
      )}
    </Box>
  );
}
